import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';
import { FaHeart, FaLanguage, FaBook, FaPlay, FaStar, FaFire, FaClock, FaTrophy, FaLock, FaUnlock, FaCrown, FaDumbbell, FaVolumeUp, FaExternalLinkAlt, FaBullhorn } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import MusicBackground from '../components/MusicBackground';
import ReportButton from '../components/reports/ReportButton';

interface Song {
  id: string;
  song_url: string;
  song_cover: string;
  creator: string;
  language: 'english' | 'spanish' | 'french';
  song_lyrics: string;
  translation: string | null;
  song_vocabulary: any;
  song_likes: number;
  is_promoted: boolean;
  is_featured: boolean;
  is_free: boolean;
  user_has_liked?: boolean;
}

interface Ad {
  id: string;
  image: string;
  title: string;
  description: string;
  link: string;
  is_active: boolean;
  created_at: string;
}

export default function Dashboard() {
  const [promotedSongs, setPromotedSongs] = useState<Song[]>([]);
  const [featuredSongs, setFeaturedSongs] = useState<Song[]>([]);
  const [popularSongs, setPopularSongs] = useState<Song[]>([]);
  const [newSongs, setNewSongs] = useState<Song[]>([]);
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionDaysLeft, setSubscriptionDaysLeft] = useState<number | null>(null);
  const navigate = useNavigate();
  const user = getCurrentUser();

  useEffect(() => {
    loadSongs();
    loadAds();
    checkAccess();
    checkSubscriptionStatus();
  }, []);

  const checkAccess = async () => {
    if (!user) return;

    try {
      if (user.is_admin) {
        setHasAccess(true);
        return;
      }

      const { data: couponUsage, error: couponError } = await supabase
        .from('stripeencouponusage')
        .select('*')
        .eq('user_id', user.id)
        .gte('valid_until', new Date().toISOString());

      if (couponError && couponError.code !== 'PGRST116') {
        throw couponError;
      }

      setHasAccess(!!couponUsage && couponUsage.length > 0);
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
    }
  };

  const checkSubscriptionStatus = async () => {
    try {
      // Check if user is authenticated first
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No authenticated user found');
        return;
      }

      const { data, error } = await supabase
        .from('stripeencouponusage')
        .select('valid_until') 
        .eq('user_id', user.id)
        .gt('valid_until', new Date().toISOString())
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No subscription found - this is normal
          console.log('No active subscription found');
        } else {
          console.error('Error checking subscription:', error);
        }
        return;
      }

      if (data) {
        setHasSubscription(true);
        
        // Calculate days left
        const validUntil = new Date(data.valid_until);
        const today = new Date();
        const diffTime = validUntil.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setSubscriptionDaysLeft(diffDays);
      } else {
        setHasSubscription(false);
        setSubscriptionDaysLeft(null);
      }
    } catch (error) {
      console.error('Network error checking subscription:', error);
      // Don't throw the error, just log it
    }
  };

  const loadAds = async () => {
    try {
      const { data, error } = await supabase
        .from('stripeads')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Randomly select 2 ads if there are more than 2
      const allAds = data || [];
      if (allAds.length <= 2) {
        setAds(allAds);
      } else {
        // Shuffle the ads array and take the first 2
        const shuffledAds = [...allAds].sort(() => Math.random() - 0.5);
        setAds(shuffledAds.slice(0, 2));
      }
    } catch (error) {
      console.error('Error loading ads:', error);
    }
  };

  const loadSongs = async () => {
    try {
      const userId = user?.id;

      // Load promoted songs
      const { data: promotedData } = await supabase
        .from('stripeensongs')
        .select('*')
        .eq('is_promoted', true)
        .eq('is_visible', true)
        .limit(3);

      // Load featured songs
      const { data: featuredData } = await supabase
        .from('stripeensongs')
        .select('*')
        .eq('is_featured', true)
        .eq('is_visible', true)
        .limit(3);

      // Load most liked songs
      const { data: popularData } = await supabase
        .from('stripeensongs')
        .select('*')
        .eq('is_visible', true)
        .order('song_likes', { ascending: false })
        .limit(3);

      // Load newest songs
      const { data: newData } = await supabase
        .from('stripeensongs')
        .select('*')
        .eq('is_visible', true)
        .order('created_at', { ascending: false })
        .limit(3);

      // If user is logged in, check which songs they've liked
      if (userId) {
        const allSongs = [...(promotedData || []), ...(featuredData || []), ...(popularData || []), ...(newData || [])];
        const songIds = allSongs.map(song => song.id);

        if (songIds.length > 0) {
          const { data: likedSongs } = await supabase
            .from('stripeen_like_songs')
            .select('song_id')
            .eq('user_id', userId)
            .in('song_id', songIds);

          const likedSongIds = new Set(likedSongs?.map(like => like.song_id) || []);

          // Add user_has_liked property to each song
          const addLikeStatus = (songs: Song[]) =>
            songs.map(song => ({
              ...song,
              user_has_liked: likedSongIds.has(song.id)
            }));

          setPromotedSongs(addLikeStatus(promotedData || []));
          setFeaturedSongs(addLikeStatus(featuredData || []));
          setPopularSongs(addLikeStatus(popularData || []));
          setNewSongs(addLikeStatus(newData || []));
        } else {
          setPromotedSongs(promotedData || []);
          setFeaturedSongs(featuredData || []);
          setPopularSongs(popularData || []);
          setNewSongs(newData || []);
        }
      } else {
        setPromotedSongs(promotedData || []);
        setFeaturedSongs(featuredData || []);
        setPopularSongs(popularData || []);
        setNewSongs(newData || []);
      }
    } catch (error) {
      console.error('Error loading songs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (songId: string) => {
    if (!user) {
      toast.error('Please log in to like songs');
      navigate('/login');
      return;
    }

    try {
      // Check if user has already liked this song
      const { data: existingLike, error: checkError } = await supabase
        .from('stripeen_like_songs')
        .select('id')
        .eq('song_id', songId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existingLike) {
        // User has already liked this song, remove the like
        const { error: deleteError } = await supabase
          .from('stripeen_like_songs')
          .delete()
          .eq('song_id', songId)
          .eq('user_id', user.id);

        if (deleteError) throw deleteError;

        // Decrease like count in songs table
        const { error: updateError } = await supabase
          .rpc('decrement_ensong_likes', { song_id: songId });

        if (updateError) throw updateError;

        // Update UI to reflect unlike
        const updateSongs = (songs: Song[]) =>
          songs.map(s => s.id === songId ? { ...s, song_likes: s.song_likes - 1, user_has_liked: false } : s);

        setPromotedSongs(updateSongs);
        setFeaturedSongs(updateSongs);
        setPopularSongs(updateSongs);
        setNewSongs(updateSongs);

        toast.success('Like removed!');
      } else {
        // User hasn't liked this song, add a like
        const { error: insertError } = await supabase
          .from('stripeen_like_songs')
          .insert({
            song_id: songId,
            user_id: user.id
          });

        if (insertError) throw insertError;

        // Increase like count in songs table
        const { error: updateError } = await supabase
          .rpc('increment_ensong_likes', { song_id: songId });

        if (updateError) throw updateError;

        // Update UI to reflect like
        const updateSongs = (songs: Song[]) =>
          songs.map(s => s.id === songId ? { ...s, song_likes: s.song_likes + 1, user_has_liked: true } : s);

        setPromotedSongs(updateSongs);
        setFeaturedSongs(updateSongs);
        setPopularSongs(updateSongs);
        setNewSongs(updateSongs);

        toast.success('Song liked!');
      }
    } catch (error) {
      console.error('Error handling like:', error);
      toast.error('Failed to update like');
    }
  };

  const handlePlay = (song: Song) => {
    if (!user) {
      toast.error('Please log in to access songs');
      navigate('/login');
      return;
    }

    if (!hasAccess && !song.is_free) {
      setShowAccessModal(true);
      return;
    }

    navigate(`/songs/${song.id}`);
  };

  const handleBuyCoupon = () => {
    navigate('/coupons');
  };

  const SongCard: React.FC<{ song: Song }> = ({ song }) => (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 hover:-translate-y-1 group">
      <div className="relative">
        <img
          src={song.song_cover}
          alt={song.creator}
          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <button
          onClick={() => handlePlay(song)}
          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
        >
          {hasAccess || song.is_free ? (
            <div className="w-16 h-16 rounded-full bg-yellow-400 bg-opacity-90 flex items-center justify-center transform scale-0 group-hover:scale-100 transition-transform duration-300">
              <FaPlay className="w-8 h-8 text-black ml-1" />
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <FaLock className="w-12 h-12 text-white mb-2" />
              <span className="text-white text-sm font-medium bg-black bg-opacity-70 px-3 py-1 rounded-full">Buy Access</span>
            </div>
          )}
        </button>
        {song.is_promoted && (
          <div className="absolute top-2 right-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-3 py-1 rounded-full text-xs font-bold flex items-center shadow-lg">
            <FaCrown className="w-3 h-3 mr-1" />
            <span>Premium</span>
          </div>
        )}
        {song.is_free && (
          <div className="absolute top-2 left-2 bg-gradient-to-r from-green-400 to-green-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center shadow-lg">
            <FaUnlock className="w-3 h-3 mr-1" />
            <span>Free</span>
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900 truncate group-hover:text-yellow-600 transition-colors duration-200">{song.creator}</h3>
          <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full">
            {song.language}
          </span>
        </div>
        <div className="flex items-center justify-between mt-4">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleLike(song.id);
            }}
            className={`flex items-center transition-colors duration-200 group ${
              song.user_has_liked ? 'text-yellow-500' : 'text-gray-600 hover:text-red-500'
            }`}
          >
            <FaHeart className={`mr-1 group-hover:scale-125 transition-transform duration-200 ${
              song.user_has_liked ? 'text-yellow-500' : ''
            }`} />
            <span>{song.song_likes}</span>
          </button>
          <div className="flex space-x-2">
            {song.translation && (
              <FaLanguage className="w-5 h-5 text-gray-400 hover:text-indigo-500 transition-colors duration-200" title="Translation available" />
            )}
            {song.song_vocabulary && (
              <FaBook className="w-5 h-5 text-gray-400 hover:text-indigo-500 transition-colors duration-200" title="Vocabulary available" />
            )}
          </div>
        </div>


        {/* Report Button */}
        <div className="mt-3 flex justify-end">
          <ReportButton
            contentId={song.id}
            contentPreview={`Song: ${song.creator} - ${song.song_lyrics?.substring(0, 100) || 'No lyrics available'}...`}
            variant="B"
            className="text-gray-400 hover:text-red-500"
          />
        </div>
      </div>
    </div>
  );

  const SongSection: React.FC<{
    title: string;
    icon: React.ReactNode;
    songs: Song[];
    loading: boolean;
    className?: string;
  }> = ({ title, icon, songs, loading, className }) => {
    const isPromoted = title === "Promoted Songs";
    
    return (
      <div className={`${className} ${isPromoted ? 'promoted-songs-container' : ''}`}>
        {/* Premium Floating Particles for Promoted Songs */}
        {isPromoted && (
          <div className="premium-particles">
            {[...Array(15)].map((_, i) => (
              <div
                key={i}
                className="premium-particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 8}s`,
                  animationDuration: `${6 + Math.random() * 4}s`
                }}
              />
            ))}
          </div>
        )}
        
        <div className={`flex items-center space-x-2 mb-6 relative ${isPromoted ? 'premium-header' : ''} ${isPromoted ? 'p-4 rounded-2xl' : ''}`}>
          <div className={`p-3 rounded-lg shadow-lg ${
            isPromoted 
              ? 'premium-icon' 
              : 'bg-gradient-to-r from-yellow-400 to-yellow-600'
          }`}>
            {icon}
          </div>
          <h2 className={`text-2xl font-bold relative section-header-glow ${
            isPromoted 
              ? 'premium-title' 
              : 'text-gray-900'
          }`}>
            {title}
            {!isPromoted && (
              <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 to-transparent rounded-full animate-pulse"></span>
            )}
          </h2>
          {isPromoted && (
            <div className="absolute -top-2 -right-2">
              <div className="relative">
                <FaStar className="text-yellow-400 w-6 h-6 animate-pulse absolute -top-1 -left-1 drop-shadow-lg" />
                <FaStar className="text-yellow-300 w-5 h-5 animate-pulse absolute top-1 -right-1 drop-shadow-lg" />
                <FaStar className="text-yellow-500 w-4 h-4 animate-pulse absolute top-3 left-1 drop-shadow-lg" />
              </div>
            </div>
          )}
          <div className="ml-auto">
            <Link 
              to="/songs" 
              className={`text-sm font-medium flex items-center transition-all duration-300 ${
                isPromoted 
                  ? 'premium-plus-link text-yellow-400 hover:text-yellow-300 px-3 py-2 rounded-lg' 
                  : 'text-indigo-600 hover:text-indigo-800'
              }`}
            >
              plus <FaPlay className="ml-1 w-3 h-3" />
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            Array(3).fill(0).map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <Skeleton height={200} />
                <div className="p-4">
                  <Skeleton count={3} />
                </div>
              </div>
            ))
          ) : songs.length > 0 ? (
            songs.map((song) => (
              <SongCard key={song.id} song={song} />
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-gray-500">
              No songs available in this section
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8 relative bg-gray-100">
      <MusicBackground />
      
      {/* Welcome Section */}
      <div className="mb-12 text-center relative">
        <h1 className="text-4xl font-bold text-gray-900 mb-4 relative inline-block">
          Welcome to Language Learning
          <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 to-transparent rounded-full"></span>
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Discover new songs, improve your language skills, and enjoy learning through music.
        </p>
      </div>

      {/* Practice Mode Banner */}
      <div className="mb-8">
        <motion.div 
          className="relative bg-gradient-to-br from-black via-gray-900 to-black rounded-2xl shadow-2xl overflow-hidden border border-gray-800"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          whileHover={{ scale: 1.02 }}
        >
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div 
              className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-yellow-400/20 to-yellow-600/20 rounded-full blur-3xl"
              animate={{ 
                scale: [1, 1.2, 1],
                rotate: [0, 180, 360]
              }}
              transition={{ 
                duration: 8,
                repeat: Infinity,
                ease: "linear"
              }}
            />
            <motion.div 
              className="absolute -bottom-10 -left-10 w-32 h-32 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-3xl"
              animate={{ 
                scale: [1.2, 1, 1.2],
                rotate: [360, 180, 0]
              }}
              transition={{ 
                duration: 10,
                repeat: Infinity,
                ease: "linear"
              }}
            />
            
            {/* Floating Particles */}
            {[...Array(15)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-yellow-400/30 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -20, 0],
                  opacity: [0.3, 1, 0.3],
                  scale: [1, 1.5, 1]
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>

          <div className="md:flex md:items-center relative z-10">
            <div className="md:flex-1 p-6 md:p-8">
              <motion.div 
                className="flex items-center mb-4"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <motion.div
                  className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center mr-4 shadow-lg"
                  whileHover={{ 
                    scale: 1.1,
                    rotate: [0, -10, 10, 0],
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <FaDumbbell className="w-6 h-6 text-black" />
                </motion.div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">Practice Mode</h2>
                  {hasSubscription && (
                    <motion.span 
                      className="inline-flex items-center bg-gradient-to-r from-yellow-400 to-yellow-600 text-black text-xs px-3 py-1 rounded-full font-bold shadow-lg"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.4 }}
                      whileHover={{ scale: 1.05 }}
                    >
                      <motion.div
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                      {subscriptionDaysLeft}d left
                    </motion.span>
                  )}
                </div>
              </motion.div>
              
              <motion.p 
                className="text-gray-300 mb-6 text-lg leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                Enhance your language skills with interactive exercises and AI-powered feedback
              </motion.p>
              
              <motion.div 
                className="flex flex-wrap gap-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                {[
                  { icon: FaBook, label: 'Vocabulary', color: 'from-blue-500/20 to-blue-600/20 border-blue-500/30' },
                  { icon: FaLanguage, label: 'Conjugation', color: 'from-purple-500/20 to-purple-600/20 border-purple-500/30' },
                  { icon: FaVolumeUp, label: 'Listening', color: 'from-green-500/20 to-green-600/20 border-green-500/30' }
                ].map((item, index) => (
                  <motion.div
                    key={item.label}
                    className={`flex items-center bg-gradient-to-r ${item.color} backdrop-blur-lg px-4 py-2 rounded-full border transition-all duration-300 hover:scale-105`}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ 
                      duration: 0.5, 
                      delay: 0.5 + (index * 0.1),
                      type: "spring",
                      stiffness: 100
                    }}
                    whileHover={{ 
                      scale: 1.05,
                      boxShadow: "0 10px 25px rgba(0,0,0,0.3)"
                    }}
                  >
                    <item.icon className="w-4 h-4 text-white mr-2" />
                    <span className="text-white text-sm font-medium">{item.label}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
            
            <motion.div 
              className="p-6 md:p-8 md:text-right"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/practice"
                  className="inline-block px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-xl font-bold hover:from-yellow-500 hover:to-yellow-700 transition-all duration-300 shadow-lg hover:shadow-yellow-400/25 transform hover:-translate-y-1"
                >
                  <span className="flex items-center">
                    Start Practicing
                    <motion.div
                      className="ml-2"
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      →
                    </motion.div>
                  </span>
                </Link>
              </motion.div>
              
              {/* Additional Action Buttons */}
              <motion.div 
                className="mt-4 flex flex-col sm:flex-row gap-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                {/* Challenge Mode Button */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/practice"
                    state={{ activeTab: 'challenge' }}
                    className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-bold hover:from-purple-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-purple-500/25 transform hover:-translate-y-1 w-full sm:w-auto"
                  >
                    <FaTrophy className="w-4 h-4 mr-2" />
                    <span className="text-sm sm:text-base">Challenge Mode</span>
                  </Link>
                </motion.div>
                
                {/* Leaderboard Button */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/practice"
                    state={{ activeTab: 'leaderboard' }}
                    className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-blue-500/25 transform hover:-translate-y-1 w-full sm:w-auto"
                  >
                    <FaCrown className="w-4 h-4 mr-2" />
                    <span className="text-sm sm:text-base">Leaderboard</span>
                  </Link>
                </motion.div>
              </motion.div>
              
              {!hasSubscription && (
                <motion.p 
                  className="mt-3 text-gray-400 text-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  Try our 1-day free trial!
                </motion.p>
              )}
            </motion.div>
          </div>
        </motion.div>
      </div>

      <div className="space-y-12">
        <SongSection
          title="Promoted Songs"
          icon={<FaStar className="w-6 h-6 text-yellow-500" />}
          songs={promotedSongs}
          loading={loading}
          className="relative overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100 p-8 rounded-3xl shadow-2xl border border-yellow-500/20"
        />

        <SongSection
          title="Featured Songs"
          icon={<FaFire className="w-6 h-6 text-red-500" />}
          songs={featuredSongs}
          loading={loading}
        />

        {/* Ads Banner - After Featured Songs */}
        {ads.length > 0 && (
          <div className="relative overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black p-6 rounded-2xl shadow-2xl border border-gray-800">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <motion.div 
                className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-3xl"
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, 180, 360]
                }}
                transition={{ 
                  duration: 8,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              <motion.div 
                className="absolute -bottom-10 -left-10 w-24 h-24 bg-gradient-to-br from-purple-400/10 to-blue-400/10 rounded-full blur-3xl"
                animate={{ 
                  scale: [1.2, 1, 1.2],
                  rotate: [360, 180, 0]
                }}
                transition={{ 
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            </div>

            <div className="flex items-center space-x-3 mb-4 relative z-10">
              <div className="p-2 rounded-lg bg-white shadow-lg">
                <FaBullhorn className="w-5 h-5 text-gray-900" />
              </div>
              <h2 className="text-xl font-bold text-white relative">
               Ads
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-blue-400 to-transparent rounded-full animate-pulse"></span>
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative z-10">
              {ads.map((ad) => (
                <motion.div
                  key={ad.id}
                  className="bg-white/10 backdrop-blur-lg rounded-lg overflow-hidden hover:bg-white/20 transition-all duration-300 hover:-translate-y-1 group border border-white/20 hover:border-blue-400/50"
                  whileHover={{ scale: 1.02 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={ad.image}
                      alt={ad.title}
                      className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute top-1 right-1 bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg">
                      Sponsored
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-base font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors duration-200 line-clamp-1">
                      {ad.title}
                    </h3>
                    <p className="text-gray-300 mb-3 text-xs leading-relaxed line-clamp-2">
                      {ad.description}
                    </p>
                    <a
                      href={ad.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-all duration-200 transform hover:scale-105 shadow-md text-xs font-medium"
                    >
                      Learn More
                      <FaExternalLinkAlt className="ml-1 w-3 h-3" />
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        <SongSection
          title="Most Popular"
          icon={<FaTrophy className="w-6 h-6 text-purple-500" />}
          songs={popularSongs}
          loading={loading}
        />

        <SongSection
          title="Recently Added"
          icon={<FaClock className="w-6 h-6 text-green-500" />}
          songs={newSongs}
          loading={loading}
        />
      </div>

      {/* Access Required Modal */}
      {showAccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Access Required</h3>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <p className="text-gray-600">
                  To access all songs and learning materials, you need to purchase a coupon worth $12.
                  Each coupon provides 30 days of unlimited access to all content on the website.
                </p>
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowAccessModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBuyCoupon}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Buy Coupon
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}